import { captureOopsError } from "@root/utils/sentry";
import { isAxiosError } from "axios";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Navigate } from "react-router-dom";

interface IErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode | ReactNode[];
  redirect?: string;
}

// Missing or forbidden resource: the expected case for a boundary with a redirect
const isExpectedHttpError = (error: unknown) =>
  isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 404);

interface IErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(error);
    if (!this.props.redirect) {
      captureOopsError(error, "boundary", errorInfo.componentStack);
    } else if (!isExpectedHttpError(error)) {
      captureOopsError(error, "redirect", errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.redirect) {
        return <Navigate to={this.props.redirect} replace={true} />;
      }
      return this.props.fallback;
    }
    return this.props.children;
  }
}
